import {gql} from "@apollo/client";
import {fetchData} from "../Service/graphQlService";
import {GET_FILE_URL} from "../Graphql/Queries/getFileUrl";

/**
 * Team members live in CONTACTS, tagged `contact_type @> '["Team"]'`.
 *
 * The older getTeamMembers / getAllTeamMembers resolvers still exist in the schema but
 * query `FROM team`, and that table has been dropped - they fail with
 * `relation "team" does not exist`. Every other call site in the app was migrated to the
 * contacts queries for the same reason.
 *
 * This is a local query rather than the shared GET_CONTACT_TEAM_LIST_QUERY because that
 * one does not select `title` or `photo`, both of which page 10 prints. The service's
 * requiredColumns list does return them, so they only need requesting.
 */
const GET_BOV_TEAM = gql`
  query getBovTeam(
    $type: String
    $pageOptions: PageOptions
    $filters: [ContactsFilters]
    $sortOptions: [SortOptions]
  ) {
    getContacts(
      type: $type
      pageOptions: $pageOptions
      filters: $filters
      sortOptions: $sortOptions
    ) {
      success
      message
      data {
        arcimedes_row_id
        contact_id
        first_name
        last_name
        title
        photo
        is_agent
        visible_on_website
        visible_on_bov
        termination_date
        deleted
      }
    }
  }
`;

/**
 * Loads the team roster printed on page 10 of the BOV.
 *
 * Two things the data model does not give us directly:
 *
 *  1. There is no department or section field on a team member, but the template groups
 *     people under LEADERSHIP / INVESTMENT TEAM / OPERATIONS & SUPPORT. The section is
 *     therefore derived from `title` and `is_agent` - see sectionFor() below.
 *
 *  2. `photo` is a file id, not a URL, so each one needs a getFile lookup. Those run in
 *     parallel and failures are tolerated: a member whose photo cannot be resolved still
 *     prints, with an empty tile, rather than failing the page.
 *
 * Uses getAllTeamMembers, NOT getTeamMembers. Both exist, but the getTeamMembers
 * resolver in strive-keystone selects an explicit column list that omits TITLE, PHOTO
 * and VISIBLE_ON_WEBSITE, so those come back null however they are requested.
 * getAllTeamMembers does `SELECT * FROM team` and returns them.
 */

export const TEAM_SECTIONS = ['LEADERSHIP', 'INVESTMENT TEAM', 'OPERATIONS & SUPPORT', 'INTERNS'];

// Titles that put someone in LEADERSHIP. "Vice President" deliberately does not - the
// template prints Steve Belli (Vice President) under INVESTMENT TEAM.
const LEADERSHIP_TITLES = [/founder/i, /managing partner/i, /\bpartner\b/i, /^president$/i, /^ceo$/i];

// Rough seniority order within LEADERSHIP, so the founder leads the row.
const LEADERSHIP_RANK = [/founder/i, /managing partner/i, /\bpartner\b/i];

/**
 * People the template prints in two sections at once, under a different role in each -
 * Steve Belli is Vice President on the investment team and Financial Consulting in
 * operations, Jacqui Green a Broker Associate and Transaction Management.
 *
 * A contact carries exactly one `title`, so the second role cannot be derived from the
 * data and is listed here. Keyed by full name, which is the only stable identifier the
 * roster query returns for a person. Replace this with a real field - a second
 * contact_type, or a department list - as soon as the backend can express it; until then
 * a leaver simply stops matching and drops out of both sections, which is the safe
 * failure.
 */
const SECOND_ROLES = {
    'Steve Belli': {section: 'OPERATIONS & SUPPORT', title: 'FINANCIAL CONSULTING'},
    'Jacqui Green': {section: 'OPERATIONS & SUPPORT', title: 'TRANSACTION MANAGEMENT'},
};

function isLeadership(title) {
    const text = String(title || '');
    if (/vice\s*president/i.test(text)) return false;
    return LEADERSHIP_TITLES.some(pattern => pattern.test(text));
}

function isIntern(title) {
    return /\bintern(s|ship)?\b/i.test(String(title || ''));
}

/**
 * Seniority within INVESTMENT TEAM. The template leads with the partnership, then the
 * vice presidents and brokers, then the associates.
 */
function investmentRank(title) {
    const text = String(title || '');
    if (/founder/i.test(text)) return 0;
    if (/managing partner/i.test(text)) return 1;
    if (/\bpartner\b/i.test(text)) return 2;
    if (/vice\s*president/i.test(text)) return 3;   // covers Senior Vice President
    if (/president/i.test(text)) return 4;
    if (/broker/i.test(text)) return 5;
    return 6;
}

/**
 * `is_agent` and the visible_* columns are character columns, so they arrive as
 * 'y'/'n'/'true' rather than booleans, and a CHAR(1) can carry padding or case the CRM
 * never shows. Everything that reads them goes through here.
 */
function flag(value) {
    if (value === true) return 'y';
    return String(value == null ? '' : value).trim().toLowerCase();
}

function isAgent(value) {
    const text = flag(value);
    return text === 'y' || text === 'yes' || text === 'true' || text === '1';
}

function isTruthyFlag(value) {
    const text = flag(value);
    return text === 'y' || text === 'yes' || text === 'true' || text === '1';
}

/** Role 'i' in roles.js - the only role offered the VISIBLE IN BOV checkbox. */
function isInternRole(member) {
    return flag(member && member.is_agent) === 'i';
}

/**
 * The sections a person appears in - more than one for some of them.
 *
 * The partnership is printed twice by design: once as LEADERSHIP and again at the head of
 * INVESTMENT TEAM, because they carry deals as well as run the firm. Interns are their own
 * section and appear nowhere else.
 */
function sectionsFor(member) {
    // The role is the authority - it is what isCurrent() gates the VISIBLE IN BOV opt-in
    // on - but not every intern record carries it, so the title is still read.
    if (isInternRole(member) || isIntern(member.title)) return ['INTERNS'];

    const sections = [];
    if (isLeadership(member.title)) {
        sections.push('LEADERSHIP', 'INVESTMENT TEAM');
    } else {
        sections.push(isAgent(member.is_agent) ? 'INVESTMENT TEAM' : 'OPERATIONS & SUPPORT');
    }
    return sections;
}

function leadershipRank(title) {
    const index = LEADERSHIP_RANK.findIndex(pattern => pattern.test(String(title || '')));
    return index === -1 ? LEADERSHIP_RANK.length : index;
}

/**
 * Current staff with a name to print. Leavers and soft-deleted records are excluded so
 * the deck never prints someone who no longer works here.
 *
 * Interns are opt-in: they print only when ticked VISIBLE IN BOV, which is the only role
 * that checkbox is offered for. Everyone else keeps the long-standing rule of being
 * excluded only when explicitly hidden from the website.
 */
function isCurrent(member) {
    if (!member) return false;
    if (member.termination_date) return false;
    const deleted = flag(member.deleted);
    if (deleted === 'y' || deleted === '1' || deleted === 'true') return false;
    if (isInternRole(member)) {
        if (!isTruthyFlag(member.visible_on_bov)) return false;
    } else if (flag(member.visible_on_website) === 'n') {
        return false;
    }
    return Boolean(member.first_name && member.last_name);
}

function fullName(member) {
    return `${member.first_name} ${member.last_name}`.trim();
}

// getFile is one request per member. Firing forty at once loses some of them, so they
// are run a few at a time and retried once before giving up on a tile.
const PHOTO_CONCURRENCY = 6;
const PHOTO_ATTEMPTS = 2;

async function resolvePhoto(photo) {
    if (!photo) return '';
    if (/^https?:\/\//.test(photo)) return photo;

    for (let attempt = 0; attempt < PHOTO_ATTEMPTS; attempt++) {
        try {
            const res = await fetchData(GET_FILE_URL, {id: photo});
            const url = res && res.data && res.data.getFile && res.data.getFile.fileUrl;
            if (url) return url;
        } catch (e) {
            // fall through to the next attempt
        }
    }
    return '';
}

/** Runs `task` over every item, at most `limit` in flight at a time. */
async function mapLimited(items, limit, task) {
    const results = new Array(items.length);
    let next = 0;

    const worker = async () => {
        while (next < items.length) {
            const index = next++;
            results[index] = await task(items[index]);
        }
    };

    await Promise.all(
        Array.from({length: Math.min(limit, items.length)}, worker)
    );
    return results;
}

/**
 * Returns [{section, members: [{name, title, photo}]}], ordered as the template prints
 * them. Sections with nobody in them are dropped.
 */
/**
 * Interns are the one role that has to be opted in, so an intern who is in the CRM but
 * not on the deck is indistinguishable from an intern who was never meant to print. When
 * every intern on the roster is dropped, say why once in the console - the alternative is
 * an empty section and no way to tell which of the four gates closed.
 *
 * A `visible_on_bov` that arrives undefined rather than null or 'y' means the column is
 * not in the resolver's select list in strive-keystone, which no amount of client-side
 * work can fix.
 */
function warnOnDroppedInterns(all, current) {
    const interns = all.filter(isInternRole);
    if (!interns.length || current.some(isInternRole)) return;

    const reasons = interns.map(member => {
        if (member.termination_date) return 'terminated';
        if (flag(member.deleted) === 'y' || flag(member.deleted) === '1') return 'deleted';
        if (member.visible_on_bov === undefined) return 'visible_on_bov not returned by the API';
        if (!isTruthyFlag(member.visible_on_bov)) return 'VISIBLE IN BOV not ticked';
        if (!(member.first_name && member.last_name)) return 'no name';
        return 'unknown';
    });

    // eslint-disable-next-line no-console
    console.warn(
        `BOV team: ${interns.length} intern(s) in the roster, none printable - ` +
        interns.map((m, i) => `${m.first_name} ${m.last_name}: ${reasons[i]}`).join('; ')
    );
}

export async function loadBovTeam() {
    const res = await fetchData(GET_BOV_TEAM, {
        type: 'Team',
        pageOptions: {page: 1, pageSize: 20000},
        filters: [],
        sortOptions: [{field: 'last_name', order: 'asc'}],
    });

    const all = (res && res.data && res.data.getContacts && res.data.getContacts.data) || [];
    const current = all.filter(isCurrent);
    warnOnDroppedInterns(all, current);
    current.sort((a, b) => String(a.last_name).localeCompare(String(b.last_name)));

    const photos = await mapLimited(current, PHOTO_CONCURRENCY, member => resolvePhoto(member.photo));

    const bySection = {};
    const add = (section, entry) => {
        if (!bySection[section]) bySection[section] = [];
        bySection[section].push(entry);
    };

    current.forEach((member, i) => {
        const name = fullName(member);
        const entry = {name, title: member.title || '', photo: photos[i], lastName: member.last_name};

        sectionsFor(member).forEach(section => add(section, entry));

        // A second appearance elsewhere, under a different role - see SECOND_ROLES.
        const second = SECOND_ROLES[name];
        if (second) add(second.section, {...entry, title: second.title});
    });

    if (bySection.LEADERSHIP) {
        bySection.LEADERSHIP.sort((a, b) => leadershipRank(a.title) - leadershipRank(b.title));
    }

    // The partnership heads the investment team, then vice presidents and brokers, then
    // the associates alphabetically.
    if (bySection['INVESTMENT TEAM']) {
        bySection['INVESTMENT TEAM'].sort((a, b) =>
            investmentRank(a.title) - investmentRank(b.title)
            || String(a.lastName).localeCompare(String(b.lastName)));
    }

    return TEAM_SECTIONS
        .filter(section => bySection[section] && bySection[section].length)
        .map(section => ({
            section,
            members: bySection[section].map(({name, title, photo}) => ({name, title, photo})),
        }));
}
